import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

// Create a context to hold the date format preference
const DateFormatContext = createContext('DD/MM/YYYY');

/**
 * Provider component to make date format available throughout the app
 */
export const DateFormatProvider = ({ children }) => {
  const dateFormatPref = useSelector((state) => state.tenantPreference.dateFormat) || 'DD/MM/YYYY';

  return (
    <DateFormatContext.Provider value={dateFormatPref}>
      {children}
    </DateFormatContext.Provider>
  );
};

/**
 * Hook to access the date format preference
 */
export const useDateFormat = () => useContext(DateFormatContext);

/**
 * Formats a date according to the specified format preference
 */
export const formatDate = (dateValue, dateFormatPref, includeTime = false, use24Hour = false) => {
  if (!dateValue) return '';

  let date;
  if (typeof dateValue === 'string') {
    date = new Date(dateValue.replace(' ', 'T')); // Fix space issue
  } else {
    date = new Date(dateValue);
  }

  if (Number.isNaN(date.getTime())) return 'Invalid Date';

  // Extract Date Parts
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  // Format Date Based on Preference
  let formattedDate;
  switch (dateFormatPref) {
    case 'MM/DD/YYYY':
      formattedDate = `${month}/${day}/${year}`;
      break;
    case 'YYYY/DD/MM':
      formattedDate = `${year}/${day}/${month}`;
      break;
    case 'YYYY/MM/DD':
      formattedDate = `${year}/${month}/${day}`;
      break;
    case 'DD-MM-YYYY':
      formattedDate = `${day}-${month}-${year}`;
      break;
    case 'MM-DD-YYYY':
      formattedDate = `${month}-${day}-${year}`;
      break;
    case 'YYYY-MM-DD':
      formattedDate = `${year}-${month}-${day}`;
      break;
    case 'DD/MM/YYYY':
    default:
      formattedDate = `${day}/${month}/${year}`;
      break;
  }

  // Add Time if Required
  if (includeTime) {
    const hours = use24Hour
      ? date.getHours().toString().padStart(2, '0')
      : (date.getHours() % 12 || 12).toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    let ampm = '';
    if (!use24Hour) {
      ampm = date.getHours() >= 12 ? ' PM' : ' AM';
    }

    formattedDate += ` ${hours}:${minutes}${ampm}`;
  }

  return formattedDate;
};

/**
 * Formats a date with time according to app preferences
 */
export const formatDateTime = (dateValue, dateFormatPref = 'DD/MM/YYYY', use24Hour = false) =>
  formatDate(dateValue, dateFormatPref, true, use24Hour);

/**
 * Standalone formatter function for components that don't have access to React context
 */
export const formatDateStandalone = (
  dateValue,
  dateFormatPref = 'DD/MM/YYYY',
  includeTime = false,
  use24Hour = false
) => formatDate(dateValue, dateFormatPref, includeTime, use24Hour);

DateFormatProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
